import "react-toastify/dist/ReactToastify.css"

import { lazy, Suspense } from "react"
import { ToastContainer } from "react-toastify"
import { QueryClient, QueryClientProvider } from "react-query"
import { createBrowserRouter, RouterProvider } from "react-router-dom"

import { ProtectedRoutes } from "./util/ProtectedRoutes"

import Layout from "./layout/Layout"
import Spinner from "./components/Spinner"

import Root from "./pages/Root/Root"
import RootHelp from "./pages/RootHelp/RootHelp"
import RootAlerts from "./pages/RootAlerts/RootAlerts"
import RootCurriculum from "./pages/RootCurriculum/RootCurriculum"
import MultiYearCurriculumAdmin from "./pages/operations/multiyearcurriculum/MultiYearCurriculumAdmin"
import RootTuitionReports from "./pages/RootTuitionReports/RootTuitionReports"
import Edit from "./pages/operations/questionBank/QuestionAdmin/EditQuestion/Edit/Edit"
import TutorAcademyCourse from "./pages/Academy/Tutor/TutorAcademyCourse"

const UpdateBookingFormPage = lazy(() =>
  import(
    /* webpackChunkName: "update-booking-form" */ "./pages/operations/auditDashboard/UpdateBookingForm"
  )
)

const UpdateBookingFormPageV2 = lazy(() =>
  import(
    /* webpackChunkName: "update-booking-form" */ "./pages/operations/auditDashboard/UpdateBookingFormV2"
  )
)

const LoginPage = lazy(() =>
  import(/* webpackChunkName: "login-page" */ "./pages/LoginPage/LoginPage")
)
const ResetPassword = lazy(() =>
  import(
    /* webpackChunkName: "reset-password" */ "./pages/ResetPassword/ResetPassword"
  )
)

const ErrorPage = lazy(() =>
  import(/* webpackChunkName: "error-page" */ "./pages/ErrorPage/ErrorPage")
)
const ForgotPassword = lazy(() =>
  import(
    /* webpackChunkName: "forgot-password" */ "./pages/ForgotPassword/ForgotPassword"
  )
)
const Expired = lazy(() =>
  import(/* webpackChunkName: "expired" */ "./pages/Expired/ExpiredPage")
)
const Library = lazy(() =>
  import(/* webpackChunkName: "library" */ "./pages/Library/Library")
)
const RootPreparation = lazy(() =>
  import(
    /* webpackChunkName: "preparation" */ "./pages/Preparation/RootPreparation"
  )
)
const Learn = lazy(() =>
  import(/* webpackChunkName: "learn" */ "./pages/Learn/LearnRefactor")
)
const TutorLearnSelect = lazy(() =>
  import(/* webpackChunkName: "learn" */ "./pages/Learn/Tutor/TutorLearnSelect")
)
const LearnSubjects = lazy(() =>
  import(/* webpackChunkName: "learnSub" */ "./pages/Learn/LearnSubjects")
)
const TutorLearnSubjects = lazy(() =>
  import(/* webpackChunkName: "learnSub" */ "./pages/Learn/Tutor/TutorLearnSubjects")
)
const Revise = lazy(() =>
  import(/* webpackChunkName: "revise" */ "./pages/Revise/Revise")
)
const TutorRevise = lazy(() =>
  import(/* webpackChunkName: "revise" */ "./pages/Revise/Tutor/TutorRevise")
)
const Profile = lazy(() =>
  import(/* webpackChunkName: "profile" */ "./pages/Profile/Profile")
)
const Simulate = lazy(() =>
  import(/* webpackChunkName: "simulate" */ "./pages/Simulate/SimulateNew")
)
const SimulateOld = lazy(() =>
  import(/* webpackChunkName: "simulate" */ "./pages/Simulate/Simulate")
)
const TutorSimulate = lazy(() =>
  import(/* webpackChunkName: "simulate" */ "./pages/Simulate/Tutor/TutorSimulate")
)
const RootAcademy = lazy(() =>
  import(/* webpackChunkName: "academy" */ "./pages/RootAcademy/RootAcademy")
)
const Essay = lazy(() =>
  import(/* webpackChunkName: "essay" */ "./pages/Essay/Essay")
)
const Tuition = lazy(() =>
  import(/* webpackChunkName: "tuition" */ "./pages/Tuition/Tuition")
)
const RootTuition = lazy(() =>
  import(/* webpackChunkName: "tuition" */ "./pages/Tuition/RootTuition")
)
const TuitionReports = lazy(() =>
  import(
    /* webpackChunkName: "tuition-reports" */ "./pages/LessonReport/TuitionReports"
  )
)
const ReportCard = lazy(() =>
  import(/* webpackChunkName: "report-card" */ "./pages/ReportCard/ReportCard")
)

const RootCourses = lazy(() =>
  import(/* webpackChunkName: "courses" */ "./pages/RootCourses/RootCourses")
)
const Subject = lazy(() =>
  import(/* webpackChunkName: "subject" */ "./pages/Learn/Subject")
)
const TutorSubject = lazy(() =>
  import(/* webpackChunkName: "subject" */ "./pages/Learn/Tutor/TutorSubject")
)
const AcademyCourse = lazy(() =>
  import(
    /* webpackChunkName: "academy-course" */ "./pages/Academy/AcademyCourse"
  )
)
const StartSimulate = lazy(() =>
  import(
    /* webpackChunkName: "start-simulate" */ "./pages/Simulate/StartSimulate"
  )
)
const RootSimulate = lazy(() =>
  import(
    /* webpackChunkName: "start-simulate" */ "./pages/Simulate/RootSimulate"
  )
)
const RootSimulateContinue = lazy(() =>
  import(
    /* webpackChunkName: "start-simulate" */ "./pages/Simulate/RootSimulateContinue"
  )
)
const ReviseSection = lazy(() =>
  import(
    /* webpackChunkName: "revise-section" */ "./pages/Revise/ReviseSection"
  )
)
const TutorReviseSection = lazy(() =>
  import(
    /* webpackChunkName: "revise-section" */ "./pages/Revise/Tutor/TutorReviseSection"
  )
)

const RootUpgrades = lazy(() =>
  import(/* webpackChunkName: "upgrades" */ "./pages/RootUpgrades/RootUpgrades")
)
const Seminars = lazy(() =>
  import(/* webpackChunkName: "seminars" */ "./pages/Seminars/Seminars")
)
const IntensiveCourses = lazy(() =>
  import(
    /* webpackChunkName: "intensive-courses" */ "./pages/Courses/IntensiveCourses"
  )
)
const Reading = lazy(() =>
  import(/* webpackChunkName: "reading" */ "./pages/Reading/Reading")
)
const Resources = lazy(() =>
  import(/* webpackChunkName: "resources" */ "./pages/Resources/Resources")
)
const RootStudentFlaggedQuestions = lazy(() =>
  import(/* webpackChunkName: "resources" */ "./pages/StudentFlaggedQuestions/StudentFlaggedQuestions")
)

const RootSeminar = lazy(() =>
  import(
    /* webpackChunkName: "RootSeminar" */ "./pages/RootSeminar/RootSeminars"
  )
)

const PDFViewer = lazy(() =>
  import(/* webpackChunkName: "pdf-viewer" */ "./pages/Library/PDFViewer")
)

const SolutionsSimulate = lazy(() =>
  import(
    /* webpackChunkName: "solutions-simulate" */ "./pages/Simulate/Solutions"
  )
)

const RootSolutionsSimulate = lazy(() =>
  import(
    /* webpackChunkName: "solutions-simulate" */ "./pages/Simulate/RootSolutions"
  )
)

const SolutionsPast = lazy(() =>
  import(
    /* webpackChunkName: "solutions-simulate" */ "./pages/Revise/PastSolutions"
  )
)

const RootPastSolutions = lazy(() =>
  import(
    /* webpackChunkName: "solutions-simulate" */ "./pages/Revise/RootPastSolutions"
  )
)

const SimulateSolutions = lazy(() =>
  import(
    /* webpackChunkName: "solutions-simulate" */ "./pages/Simulate/StudentAdminSolutions"
  )
)

const RootSimulateSolutions = lazy(() =>
  import(
    /* webpackChunkName: "solutions-simulate" */ "./pages/Simulate/RootSimulateSolutions"
  )
)

const SolutionsRevise = lazy(() =>
  import(
    /* webpackChunkName: "solutions-simulate" */ "./pages/Revise/Solutions"
  )
)

const AuditDashboard = lazy(() =>
  import("./pages/operations/auditDashboard/AuditDashboard")
)

const AuditDashboardV2 = lazy(() =>
  import("./pages/operations/auditDashboard/AuditDashboardV2")
)

const RCTrackingDashboard = lazy(() =>
  import("./pages/operations/rcTrackingDashboard/RCTrackingDashboard")
)

const LabelManagement = lazy(() =>
  import("./pages/operations/labels/LabelManagement")
)

const RootSubmissions = lazy(() =>
  import("./pages/RootSubmissions/RootSubmissions")
)

const EditAcademyContent = lazy(() =>
  import("./pages/operations/academy/EditContent")
)

const Sales = lazy(() => import("./pages/operations/sales/Sales"))
const SalesV2 = lazy(() => import("./pages/operations/sales/SalesV2"))
const RootBookingForm = lazy(() => import("./pages/operations/sales/RootBookingForm"))
const UpsellForm = lazy(() => import("./pages/operations/sales/UpsellForm"))
const Blogs = lazy(() => import("./pages/operations/blogs/Blogs"))
const ReadingList = lazy(() =>
  import("./pages/operations/readingList/ReadingList")
)
const QuestionBank = lazy(() =>
  import("./pages/operations/questionBank/QuestionBank")
)
const QuestionAdmin = lazy(() =>
  import("./pages/operations/questionBank/QuestionAdmin/QuestionAdmin")
)
const FlaggedQuestions = lazy(() =>
  import("./pages/operations/questionBank/FlaggedQuestions/FlaggedQuestions")
)
const AdminView = lazy(() =>
  import("./pages/operations/seminars/AdminView/AdminView")
)
const TutorView = lazy(() => import("./pages/TutorPortal/Seminars/Seminars"))

const EditQuestion = lazy(() =>
  import(
    "./pages/operations/questionBank/QuestionAdmin/EditQuestion/EditQuestion"
  )
)

const CreateQuestion = lazy(() =>
  import(
    "./pages/operations/questionBank/QuestionAdmin/CreateQuestion/CreateQuestion"
  )
)

const EditSubject = lazy(() =>
  import(
    "./pages/operations/questionBank/QuestionAdmin/EditSubject/EditSubject"
  )
)

const EditSubTopic = lazy(() =>
  import(
    "./pages/operations/questionBank/QuestionAdmin/EditSubTopic/EditSubTopic"
  )
)

const EditExam = lazy(() =>
  import("./pages/operations/questionBank/QuestionAdmin/EditExam/EditExam")
)

const LearnAdmin = lazy(() =>
  import("./pages/operations/questionBank/Learn/Learn")
)

const EditContent = lazy(() =>
  import("./pages/operations/questionBank/Learn/EditContent/EditContent")
)

const AdminSeminars = lazy(() =>
  import("./pages/operations/seminars/AdminView/Seminar/Seminar")
)

const Notifications = lazy(() =>
  import("./pages/operations/notifications/Notifications")
)

const Accounts = lazy(() => import("./pages/operations/accounts/Accounts"))

const Score = lazy(() => import("./pages/operations/accounts/Score/Score"))

const Announcements = lazy(() =>
  import("./pages/operations/announcements/Announcements")
)

const StudentsFlaggedQuestionsTutor = lazy(() =>
  import(
    "./pages/TutorPortal/TutorDashboard/StudentsFlaggedQuestions/StudentsFlaggedQuestions"
  )
)
const ViewFlaggedQuestionTutor = lazy(() =>
  import(
    "./pages/TutorPortal/TutorDashboard/ViewFlaggedQuestion/ViewFlaggedQuestion"
  )
)

const SeminarsTutor = lazy(() =>
  import("./pages/TutorPortal/Seminars/Seminars")
)

const EditRevise = lazy(() => import("./pages/Revise/EditRevise"))

const RootEditRevise = lazy(() => import("./pages/Revise/RootEditRevise"))

const ParentCalendar = lazy(() =>
  import("./pages/ParentPortal/Calendar/ParentCalendar")
)
const StudentSummary = lazy(() =>
  import("./pages/SchoolPortal/StudentSummary/StudentSummary")
)

const GetReports = lazy(() => import("./pages/LessonReport/GetReport"))

const SeminarReports = lazy(() =>
  import(
    /* webpackChunkName: "error-page" */ "./pages/operations/seminars/ViewSeminarReport"
  )
)

const QuestionAdminReport = lazy(() =>
  import(
    /* webpackChunkName: "error-page" */ "./pages/operations/questionBank/QuestionAdmin/QuestionAdminReport/QuestionAdminReport"
  )
)

const AdminLibrary = lazy(() =>
  import(
    /* webpackChunkName: "admin-libray" */ "./pages/operations/library/Library"
  )
)

const AddBook = lazy(() =>
  import("./pages/operations/library/add-book/AddBook")
)

const EditBook = lazy(() =>
  import("./pages/operations/library/edit-book/EditBook")
)

const EditTutor = lazy(() => import("./pages/operations/accounts/EditTutor"))

const DiagnosticTest = lazy(() => import("./pages/DiagnosticTest/DiagnosticTest"))

const DiagnosticTestResults = lazy(() => import("./pages/DiagnosticTest/DiagnosticTestResult/DiagnosticTestResult"))

const DiagnosticTestResultsV2 = lazy(() => import("./pages/DiagnosticTest/DiagnosticTestResult/DiagnosticTestResultV2"))

const RootTargets = lazy(() =>
  import(/* webpackChunkName: "targets" */ "./pages/Targets/RootTargets")
)

const LessonPlansAndGuides = lazy(() =>
  import(/* webpackChunkName: "lesson-plans-and-guides" */ "./pages/LessonPlansAndGuides/LessonPlansAndGuides")
)

const DownloadPastPapers = lazy(() =>
  import(/* webpackChunkName: "download-past-papers" */ "./pages/DownloadPastPapers/DownloadPastPapers")
)

const TutorHelp = lazy(() =>
  import(/* webpackChunkName: "tutor-help" */ "./pages/TutorHelp/TutorHelp")
)

const ManageTutorDownloads = lazy(() =>
  import(/* webpackChunkName: "manage-tutor-downloads" */ "./pages/operations/tutorDownloads/TutorDownload")
)

const ManageTutorFAQs = lazy(() =>
  import(/* webpackChunkName: "manage-tutor-faqs" */ "./pages/operations/tutorFAQs/ManageTutorFAQs")
)

const FlaggedQuestionPreview = lazy(() =>
  import(/* webpackChunkName: "flagged-question-preview" */ "./pages/TutorPortal/TutorDashboard/ViewFlaggedQuestion/FlaggedQuestionPreview")
)

function App() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: false,
        refetchOnWindowFocus: false
      }
    }
  })

  const protectedChildRoutes = [
    {
      path: "/question-bank/question-admin/report",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <QuestionAdminReport />
        </Suspense>
      )
    },
    {
      path: "/",
      element: <Root />
    },
    {
      path: "/curriculum",
      element: <RootCurriculum />
    },
    {
      path: "/multi-year-curriculum-admin",
      element: <MultiYearCurriculumAdmin />
    },
    {
      path: "/manage-library",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <AdminLibrary />
        </Suspense>
      )
    },
    {
      path: "/manage-library/add-book",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <AddBook />
        </Suspense>
      )
    },
    {
      path: "/manage-library/edit/:id",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <EditBook />
        </Suspense>
      )
    },
    {
      path: "/my-application-&-account",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <Profile />
        </Suspense>
      )
    },
    {
      path: "/resources/library",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <Library />
        </Suspense>
      )
    },
    {
      path: "/resources/library/:book/:id",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <PDFViewer />
        </Suspense>
      )
    },
    {
      path: "/academy",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <RootAcademy />
        </Suspense>
      )
    },
    {
      path: "/academy/:course",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <AcademyCourse />
        </Suspense>
      )
    },
    {
      path: "/academy/tutor/:course",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <TutorAcademyCourse />
        </Suspense>
      )
    },
    {
      path: "/resources/question-bank",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <RootPreparation />
        </Suspense>
      )
    },
    {
      path: "/resources/question-bank/learn",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <Learn />
        </Suspense>
      )
    },
    {
      path: "/resources/tutor/question-bank/learn",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <TutorLearnSelect />
        </Suspense>
      )
    },
    {
      path: "/resources/question-bank/learn/:subject",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <LearnSubjects />
        </Suspense>
      )
    },
    {
      path: "/resources/tutor/question-bank/learn/:subject",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <TutorLearnSubjects />
        </Suspense>
      )
    },
    {
      path: "/resources/question-bank/learn/:subject/:id",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <Subject />
        </Suspense>
      )
    },
    {
      path: "/resources/tutor/question-bank/learn/:subject/:id",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <TutorSubject />
        </Suspense>
      )
    },
    {
      path: "/resources/question-bank/revise",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <Revise />
        </Suspense>
      )
    },
    {
      path: "/resources/tutor/question-bank/revise",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <TutorRevise />
        </Suspense>
      )
    },
    {
      path: "/resources/question-bank/revise/edit-revise/:id",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <RootEditRevise />
        </Suspense>
      )
    },
    {
      path: "/resources/question-bank/revise/:subject",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <ReviseSection />
        </Suspense>
      )
    },
    {
      path: "/resources/tutor/question-bank/revise/:subject",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <TutorReviseSection />
        </Suspense>
      )
    },
    {
      path: "/resources/question-bank/revise/:subject/:start",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <StartSimulate />
        </Suspense>
      )
    },
    {
      path: "/resources/question-bank/simulate/solutions/:id",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <RootSolutionsSimulate />
        </Suspense>
      )
    },
    {
      path: "/resources/question-bank/revise/solutions",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <SolutionsRevise />
        </Suspense>
      )
    },
    {
      path: "/resources/question-bank/revise/past-solutions",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <RootPastSolutions />
        </Suspense>
      )
    },
    {
      path: "/resources/question-bank/simulate/solutions",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <RootSimulateSolutions />
        </Suspense>
      )
    },
    {
      path: "/resources/question-bank/simulateold",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <SimulateOld />
        </Suspense>
      )
    },
    {
      path: "/resources/question-bank/simulate",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <Simulate />
        </Suspense>
      )
    },
    {
      path: "/resources/tutor/question-bank/simulate",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <TutorSimulate />
        </Suspense>
      )
    },
    {
      path: "/resources/question-bank/simulate/:start",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <RootSimulate />
        </Suspense>
      )
    },
    {
      path: "/resources/submissions",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <Essay />
        </Suspense>
      )
    },
    {
      path: "/tuition",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <RootTuition />
        </Suspense>
      )
    },
    {
      path: "/courses",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <RootCourses />
        </Suspense>
      )
    },
    {
      path: "/courses/:course/:id",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <IntensiveCourses />
        </Suspense>
      )
    },

    {
      path: "/seminars/:seminar/:id",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <Seminars />
        </Suspense>
      )
    },

    {
      path: "/upgrades",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <RootUpgrades />
        </Suspense>
      )
    },
    {
      path: "/resources/reading",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <Reading />
        </Suspense>
      )
    },
    {
      path: "/resources/student-flagged-questions",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <RootStudentFlaggedQuestions />
        </Suspense>
      )
    },
    {
      path: "/resources",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <Resources />
        </Suspense>
      )
    },
    {
      path: "/tuition/tuition-reports",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <TuitionReports />
        </Suspense>
      )
    },
    {
      path: "/tuition/tuition-reports/:id",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <GetReports />
        </Suspense>
      )
    },
    {
      path: "/tuition-reports/report/:id",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <GetReports />
        </Suspense>
      )
    },
    {
      path: "/report-card",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <ReportCard />
        </Suspense>
      )
    },
    {
      path: "/help",
      element: <RootHelp />
    },

    {
      path: "/audit-dashboard",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <AuditDashboardV2 />
        </Suspense>
      )
    },
    {
      path: "/report-card-tracking-dashboard",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <RCTrackingDashboard />
        </Suspense>
      )
    },
    {
      path: "/label-management",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <LabelManagement />
        </Suspense>
      )
    },
    {
      path: "/update-booking-form",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <UpdateBookingFormPageV2 />
        </Suspense>
      )
    },

    {
      path: "/Academy/:academyName/:id",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <EditAcademyContent />
        </Suspense>
      )
    },
    {
      path: "/sales",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <RootBookingForm />
        </Suspense>
      )
    },
    {
      path: "/sales/new-customer",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <SalesV2 />
        </Suspense>
      )
    },
    {
      path: "/sales/upsell",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <UpsellForm />
        </Suspense>
      )
    },
    {
      path: "/blogs",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <Blogs />
        </Suspense>
      )
    },
    {
      path: "/reading-list",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <ReadingList />
        </Suspense>
      )
    },
    {
      path: "/question-bank",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <QuestionBank />
        </Suspense>
      )
    },
    {
      path: "/question-bank/learn",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <LearnAdmin />
        </Suspense>
      )
    },
    {
      path: "/question-bank/learn/:sub/:id",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <EditContent />
        </Suspense>
      )
    },
    {
      path: "/question-bank/question-admin",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <QuestionAdmin />
        </Suspense>
      )
    },
    {
      path: "/question-bank/question-admin/edit-question",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <EditQuestion />
        </Suspense>
      )
    },
    {
      path: "/question-bank/question-admin/edit-question/:id",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <Edit />
        </Suspense>
      )
    },
    {
      path: "/question-bank/question-admin/create-question",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <CreateQuestion />
        </Suspense>
      )
    },
    {
      path: "/question-bank/question-admin/create_~_edit-subject",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <EditSubject />
        </Suspense>
      )
    },
    {
      path: "/question-bank/question-admin/create_~_edit-sub-topic",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <EditSubTopic />
        </Suspense>
      )
    },
    {
      path: "/question-bank/question-admin/create_~_edit-exam",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <EditExam />
        </Suspense>
      )
    },
    {
      path: "/question-bank/question-admin/flagged-questions",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <FlaggedQuestions />
        </Suspense>
      )
    },
    {
      path: "/seminars/admin-view",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <AdminView />
        </Suspense>
      )
    },
    {
      path: "/seminars/seminar-report",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <SeminarReports />
        </Suspense>
      )
    },
    {
      path: "/seminars/:name/:id/:uid",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <TutorView />
        </Suspense>
      )
    },

    {
      path: "/seminars",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <RootSeminar />
        </Suspense>
      )
    },

    {
      path: "/seminars/admin-view/:degree/:id",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <AdminSeminars />
        </Suspense>
      )
    },
    {
      path: "/notifications",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <Notifications />
        </Suspense>
      )
    },

    {
      path: "/alerts",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <RootAlerts />
        </Suspense>
      )
    },
    {
      path: "/accounts",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <Accounts />
        </Suspense>
      )
    },
    {
      path: `/accounts/edit/tutor/:id`,
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <EditTutor />
        </Suspense>
      )
    },
    {
      path: "/accounts/score",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <Score />
        </Suspense>
      )
    },
    {
      path: "/announcements",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <Announcements />
        </Suspense>
      )
    },

    {
      path: "/tutor/:name/:id",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <StudentsFlaggedQuestionsTutor />
        </Suspense>
      )
    },
    {
      path: "/tutor/flagged-question/:id",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <ViewFlaggedQuestionTutor />
        </Suspense>
      )
    },
    {
      path: "/flagged-question-view/:id",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <FlaggedQuestionPreview />
        </Suspense>
      )
    },
    {
      path: "/submissions",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <RootSubmissions />
        </Suspense>
      )
    },
    {
      path: "/tutor/seminars",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <SeminarsTutor />
        </Suspense>
      )
    },

    {
      path: "*",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <ErrorPage />
        </Suspense>
      )
    },

    {
      path: "/calendar",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <ParentCalendar />
        </Suspense>
      )
    },

    {
      path: "/tuition-reports",
      element: <RootTuitionReports />
    },
    {
      path: "/:dashboard/:id",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <StudentSummary />
        </Suspense>
      )
    },
    {
      path: "/diagnostic-test-results",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <DiagnosticTestResults />
        </Suspense>
      )
    },
    {
      path: "/diagnostic-test-results/:id",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <DiagnosticTestResults />
        </Suspense>
      )
    },
    {
      path: "/diagnostic-test-results-v2/:id",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <DiagnosticTestResultsV2 />
        </Suspense>
      )
    },
    {
      path: "/tutor-targets",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <RootTargets />
        </Suspense>
      )
    },
    {
      path: "/resources/lesson-plans-and-guides",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <LessonPlansAndGuides />
        </Suspense>
      )
    },
    {
      path: "/resources/download-past-papers",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <DownloadPastPapers />
        </Suspense>
      )
    },
    {
      path: "/tutor-help",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <TutorHelp />
        </Suspense>
      )
    },
    {
      path: "/manage-lesson-plans-and-guides",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <ManageTutorDownloads />
        </Suspense>
      )
    },
    {
      path: "/manage-tutor-faqs",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <ManageTutorFAQs />
        </Suspense>
      )
    },
    {
      path: "/resources/question-bank/simulate/continue/:start",
      element: (
        <Suspense fallback={<Spinner type="child" />}>
          <RootSimulateContinue />
        </Suspense>
      )
    }
  ]

  const protectedRoutes = {
    element: <ProtectedRoutes />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "/",
        element: <Layout />,
        children: protectedChildRoutes
      }
    ]
  }

  const routesList = [
    {
      path: "/login",
      element: (
        <Suspense fallback={<Spinner />}>
          <LoginPage />
        </Suspense>
      )
    },
    {
      path: "/expired",
      element: (
        <Suspense fallback={<Spinner />}>
          <Expired />
        </Suspense>
      )
    },
    {
      path: "/forgot-password",
      element: (
        <Suspense fallback={<Spinner />}>
          <ForgotPassword />
        </Suspense>
      )
    },
    {
      path: "/auth/reset-password",
      element: (
        <Suspense fallback={<Spinner />}>
          <ResetPassword />
        </Suspense>
      )
    },
    {
      path: "/diagnostic-test",
      element: <ProtectedRoutes />,
      errorElement: <ErrorPage />,
      children: [
        {
          path: "/diagnostic-test",
          element: <DiagnosticTest />
        }
      ]
    },
    protectedRoutes
  ]

  const router = createBrowserRouter(routesList)


  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <ToastContainer />
    </QueryClientProvider>
  )
}

export default App
